/**
 * Profile session + signed-out guard
 * ------------------------------------------------------------------
 * The identity model, in one place.
 *
 * A shopper who signs in with a phone and an OTP is logged into the custom
 * profile app, not into Shopify: `POST /v1/auth/phone/verify` sets the app's
 * own session cookie, and the store is on Basic, so there is no multipass to
 * mint a native customer session from it. Liquid's `customer` object is
 * therefore nil for every OTP shopper, and any surface that decides "signed
 * in?" from Liquid tells them they are signed out forever.
 *
 * So the question is asked of the app instead:
 *
 *   GET <base>/v1/auth/session -> { authenticated, customer? }
 *
 * and the answer drives three states, which is one more than a boolean can
 * carry and the reason this is not inlined into each section:
 *
 *   authenticated  reveal the surface and let assets/pf-profile-hydrate.js
 *                  fill it from the app
 *   anonymous      send them to the sign-in screen with ?return_to= so they
 *                  come back to the page they asked for
 *   unreachable    no base configured, DNS/CORS/offline, or the app answering
 *                  5xx. NOT a signed-out shopper, so never a redirect: the
 *                  page falls back to the sign-in card it already rendered.
 *
 * A native Shopify session (the email login, still reachable) is respected as
 * well: when Liquid rendered a real `customer`, the gate ships in the `ready`
 * state and nothing here takes that away, whatever the app says.
 *
 * The section markup this drives:
 *   [data-pf-profile-gate]        the card holder, carrying the initial state
 *   [data-pf-gate-checking]         "checking your session" placeholder
 *   [data-pf-gate-guest]            the signed-out card
 *   [data-pf-profile-body]        the surface itself, a sibling of the gate
 *
 * Emitted by snippets/pf-profile-boot.liquid + snippets/profile-data.liquid.
 */
(function () {
  'use strict';

  var HIDDEN_CLASS = 'pf-gate-hidden';

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  /* ---------------------------------------------------------------- config */

  var configCache = null;

  /**
   * Everything the profile stack knows that only Liquid could tell it: where
   * the sibling pages live, how this shop writes money, which icons to use.
   * Printed once per page by snippets/pf-profile-boot.liquid.
   */
  function config() {
    if (configCache) return configCache;
    configCache = {};
    var node = document.querySelector('[data-pf-profile-config]');
    if (node) {
      try {
        configCache = JSON.parse(node.textContent) || {};
      } catch (error) {
        configCache = {};
      }
    }
    return configCache;
  }

  /* --------------------------------------------------------------- session */

  var sessionPromise = null;

  function classify(error) {
    /* NOT_CONNECTED is "the app was not reached", never "you are signed out";
       and a 5xx is the app failing, not refusing. Redirecting on either would
       bounce a signed-in shopper out of their own profile. */
    if (!error || error.notConnected) return 'unreachable';
    if (error.status >= 500) return 'unreachable';
    return 'anonymous';
  }

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function session() {
    if (sessionPromise) return sessionPromise;

    var client = window.PFApi;
    if (!client || !client.isConfigured()) {
      sessionPromise = Promise.resolve({ state: 'unreachable', customer: null });
    } else {
      // App deploys and edge blips answer with a 502 page, not the app's
      // JSON; that must not read as "you are signed out" or a permanent
      // failure. Retry twice with a short backoff before reporting unreachable.
      sessionPromise = (function probe(attempt) {
        return client
          .get('/v1/auth/session')
          .then(function (body) {
            var authenticated = !!(body && (body.authenticated || body.customer));
            return {
              state: authenticated ? 'authenticated' : 'anonymous',
              customer: (body && body.customer) || null
            };
          })
          .catch(function (error) {
            if (attempt < 2 && (error.notConnected || error.status >= 500)) {
              return delay(1500 * (attempt + 1)).then(function () {
                return probe(attempt + 1);
              });
            }
            return { state: classify(error), customer: null, error: error };
          });
      })(0);
    }

    sessionPromise.then(function (result) {
      document.dispatchEvent(
        new CustomEvent('pf:profile:session', { detail: result })
      );
    });

    return sessionPromise;
  }

  /* ------------------------------------------------------------------ gate */

  function bodies(gate) {
    var scope = gate.parentElement || document;
    return scope.querySelectorAll('[data-pf-profile-body]');
  }

  function signInHref(gate) {
    var target = gate.getAttribute('data-pf-gate-signup') || '';
    if (!target) return '';
    var here = window.location.pathname + window.location.search;
    return (
      target +
      (target.indexOf('?') === -1 ? '?' : '&') +
      'return_to=' +
      encodeURIComponent(here)
    );
  }

  function settle(gate, signedIn) {
    var checking = gate.querySelector('[data-pf-gate-checking]');
    var guest = gate.querySelector('[data-pf-gate-guest]');

    if (checking) checking.hidden = true;
    if (guest) guest.hidden = signedIn;

    each(bodies(gate), function (node) {
      node.classList.toggle(HIDDEN_CLASS, !signedIn);
    });

    gate.setAttribute('data-pf-gate-state', signedIn ? 'ready' : 'guest');
  }

  function reportUnreachable(gate) {
    var slot = gate.querySelector('[data-pf-gate-error]');
    if (!slot) return;
    slot.textContent = window.PFApi
      ? window.PFApi.NOT_CONNECTED_MESSAGE
      : 'This is not connected yet.';
    slot.hidden = false;
  }

  function guard(gate) {
    if (gate.__pfGateBound) return;
    gate.__pfGateBound = true;

    var initial = gate.getAttribute('data-pf-gate-state');

    /* 'guest' means Liquid already knew the answer — no app configured and no
       native customer — and said so. There is nothing to ask. */
    if (initial === 'guest') { var destination = signInHref(gate); if (destination) window.location.replace(destination); return; }

    session().then(async function (result) {
      if (result.state === 'authenticated') {
        settle(gate, true);
        return;
      }

      /* A native email login stands on its own: the page is already rendered
         from Liquid's customer and the app not knowing them changes nothing. */
      if (initial === 'ready') {
        settle(gate, true);
        return;
      }

      if (result.state === 'anonymous' && window.PFApi && await window.PFApi.resumeSession()) return;

      if (result.state === 'anonymous' && gate.getAttribute('data-pf-gate-mode') === 'redirect') {
        var href = signInHref(gate);
        if (href) {
          // replace(), so Back does not bounce off this page again.
          window.location.replace(href);
          return;
        }
      }

      settle(gate, false);
      if (result.state === 'unreachable') reportUnreachable(gate);
    });
  }

  /* ---------------------------------------------------------------- logout */

  /**
   * Signing out has two halves once the app is in play. The link points at
   * Shopify's own /account/logout, which is all a native session needs and
   * does nothing at all for an OTP shopper: their session is the app's cookie,
   * and only POST /v1/auth/logout clears it. Without this, "Logout" leaves
   * them signed in and the guard waves them straight back through.
   */
  function bindLogout(link) {
    if (link.__pfLogoutBound) return;
    link.__pfLogoutBound = true;

    link.addEventListener('click', function (event) {
      var client = window.PFApi;
      if (!client || !client.isConfigured() || link.__pfLoggingOut) return;

      event.preventDefault();
      link.__pfLoggingOut = true;

      var leave = function () {
        sessionPromise = null;
        window.location.assign(link.href);
      };
      // Either way they leave: a refused logout must not trap them here.
      client.post('/v1/auth/logout', {}).then(leave, leave);
    });
  }

  function boot(root) {
    var scope = root || document;
    each(scope.querySelectorAll('[data-pf-profile-gate]'), guard);
    each(scope.querySelectorAll('[data-pf-logout]'), bindLogout);
  }

  window.PFProfileSession = {
    get: session,
    config: config,
    /** Re-asks the app — used after a write that could change the session. */
    refresh: function () {
      sessionPromise = null;
      return session();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
})();
